// Same widget as US stocks SEO widgets
// TODO refactor this widget to be more scalable
import classNames from 'classnames'
import {
  FloatingPortal,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  arrow,
} from '@floating-ui/react'
import React, { useRef, useState } from 'react'
import InStockL2Heading from '../Pages/InStockDetailPageV2/InL2Heading'
import { Insight, InsightsData } from './types'
import { StencilCDNImage } from '@/components/Common/StencilImage'
import Carousel from '@/stories/components/Common/Carousel/Carousel'

export default React.memo(function INDStocksInsights({
  data,
  bgColor = 'bg-brand-white',
  showDesc = true,
  itemClassName,
}: {
  data?: InsightsData
  bgColor?: string
  showDesc?: boolean
  itemClassName?: string
}) {
  if (!data?.heading) {
    return null
  }

  return (
    <div className="bg-brand-white">
      {data.heading && (
        <InStockL2Heading
          heading={data.heading}
          subheading={showDesc ? data.description : ''}
        />
      )}

      <Carousel
        showArrows
        stopAutoPlay
        dots
        showDotsOnMobileOnly
        showArrowsOnDesktop
      >
        <ul className={classNames('mb-6 mt-4 flex gap-3', itemClassName)}>
          {!!data.insights?.length &&
            data.insights.map((el, index) => {
              return (
                <InsightItem
                  item={el}
                  key={`${el.title}-${index}`}
                  bgColor={bgColor}
                />
              )
            })}
        </ul>
      </Carousel>
    </div>
  )
})

function InsightItem({
  bgColor = 'bg-brand-white',
  item,
}: {
  bgColor: string
  item: Insight
}) {
  const [showTooltip, setShowTooltip] = useState(false)
  const arrowRef = useRef(null)

  const { x, y, strategy, context, reference, floating, middlewareData } =
    useFloating({
      placement: 'top',
      middleware: [arrow({ element: arrowRef }), offset(10), flip(), shift()],
      open: showTooltip,
      onOpenChange: setShowTooltip,
    })

  const click = useClick(context)
  const hover = useHover(context)
  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    hover,
    dismiss,
  ])

  if (!item.title) {
    return null
  }

  return (
    <li
      className={classNames(
        'min-w-full rounded-xl border p-2.5 md:p-4 lg:min-w-[47%] xl:min-w-[32%]',
        bgColor,
      )}
      style={{
        borderColor: item.tag?.txt_color ?? '',
      }}
    >
      <div className="flex gap-2">
        {item.image?.png && (
          <aside className="flex-shrink-0">
            <StencilCDNImage
              src={item.image.png}
              width={44}
              height={44}
              className="p-0"
            />{' '}
          </aside>
        )}

        <section>
          {item.tag && (
            <div
              className="mb-2 flex w-fit items-center rounded-md border-[0.5px]"
              style={{
                backgroundColor: item.tag.bg_color ?? '',
              }}
            >
              {item.tag.image?.png && (
                <StencilCDNImage
                  src={item.tag.image.png}
                  width={22}
                  height={22}
                />
              )}
              <span
                className="px-2 text-xs font-medium"
                style={{ color: item.tag.txt_color ?? '' }}
              >
                {item.tag.title}
              </span>
            </div>
          )}
          <div className="flex items-center font-medium">
            <p>{item.title}</p>

            {item.info_icon_image?.png && (
              <p
                className="ml-auto flex-shrink-0"
                ref={reference}
                {...getReferenceProps()}
              >
                <StencilCDNImage
                  src={item.info_icon_image.png}
                  width={14}
                  height={14}
                />
              </p>
            )}
            {showTooltip && item.info_icon_content && (
              <FloatingPortal>
                <div
                  ref={floating}
                  {...getFloatingProps()}
                  id="us-stocks-widget-tooltip"
                  style={{
                    position: strategy,
                    top: y ?? 0,
                    left: x ?? 0,
                  }}
                  className="bg-brand-white shadow-three relative z-50 w-80 rounded-lg p-4"
                >
                  <p className="text-xs">{item.info_icon_content}</p>

                  <div
                    ref={arrowRef}
                    style={{
                      top: middlewareData.arrow?.y
                        ? `${middlewareData.arrow.y}px`
                        : '',
                      left: middlewareData.arrow?.x
                        ? `${middlewareData.arrow.x}px`
                        : '',
                    }}
                    className="bg-brand-white pointer-events-none absolute -bottom-2 h-4 w-4 rotate-45 transform border-b-2 border-r-2 border-gray-100"
                  />
                </div>
              </FloatingPortal>
            )}
          </div>
          {/* <p className="text-xs mt-[1px]">{item}</p> */}
          <p className="mt-2 text-sm">{item.description}</p>
        </section>
      </div>
    </li>
  )
}
